import { useEffect, useRef } from 'react';
import { useConsoleStore } from '../store/consoleStore';

interface OriginalConsole {
  log: typeof console.log;
  info: typeof console.info;
  warn: typeof console.warn;
  error: typeof console.error;
  debug: typeof console.debug;
  table: typeof console.table;
  group: typeof console.group;
  groupCollapsed: typeof console.groupCollapsed;
  groupEnd: typeof console.groupEnd;
  clear: typeof console.clear;
}

export function useConsoleInterceptor(enabled: boolean = true) {
  const originalConsoleRef = useRef<OriginalConsole | null>(null);

  // Get store actions using selectors to avoid re-renders
  const log = useConsoleStore((state) => state.log);
  const info = useConsoleStore((state) => state.info);
  const warn = useConsoleStore((state) => state.warn);
  const error = useConsoleStore((state) => state.error);
  const debug = useConsoleStore((state) => state.debug);
  const table = useConsoleStore((state) => state.table);
  const group = useConsoleStore((state) => state.group);
  const groupCollapsed = useConsoleStore((state) => state.groupCollapsed);
  const groupEnd = useConsoleStore((state) => state.groupEnd);
  const clear = useConsoleStore((state) => state.clear);

  useEffect(() => {
    if (!enabled) return;

    // Store original console methods
    if (!originalConsoleRef.current) {
      originalConsoleRef.current = {
        log: console.log.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        debug: console.debug.bind(console),
        table: console.table.bind(console),
        group: console.group.bind(console),
        groupCollapsed: console.groupCollapsed.bind(console),
        groupEnd: console.groupEnd.bind(console),
        clear: console.clear.bind(console),
      };
    }

    const original = originalConsoleRef.current;

    // Override console methods
    console.log = (...args: unknown[]) => {
      original.log(...args);
      log(...args);
    };

    console.info = (...args: unknown[]) => {
      original.info(...args);
      info(...args);
    };

    console.warn = (...args: unknown[]) => {
      original.warn(...args);
      warn(...args);
    };

    console.error = (...args: unknown[]) => {
      original.error(...args);
      error(...args);
    };

    console.debug = (...args: unknown[]) => {
      original.debug(...args);
      debug(...args);
    };

    console.table = (data: unknown, columns?: string[]) => {
      original.table(data, columns);
      table(data, columns);
    };

    console.group = (label?: string) => {
      original.group(label);
      group(label);
    };

    console.groupCollapsed = (label?: string) => {
      original.groupCollapsed(label);
      groupCollapsed(label);
    };

    console.groupEnd = () => {
      original.groupEnd();
      groupEnd();
    };

    console.clear = () => {
      original.clear();
      clear();
    };

    // Cleanup - restore original console methods
    return () => {
      if (originalConsoleRef.current) {
        console.log = originalConsoleRef.current.log;
        console.info = originalConsoleRef.current.info;
        console.warn = originalConsoleRef.current.warn;
        console.error = originalConsoleRef.current.error;
        console.debug = originalConsoleRef.current.debug;
        console.table = originalConsoleRef.current.table;
        console.group = originalConsoleRef.current.group;
        console.groupCollapsed = originalConsoleRef.current.groupCollapsed;
        console.groupEnd = originalConsoleRef.current.groupEnd;
        console.clear = originalConsoleRef.current.clear;
      }
    };
  }, [enabled, log, info, warn, error, debug, table, group, groupCollapsed, groupEnd, clear]);

  // Return original console for direct access if needed
  return originalConsoleRef.current;
}
